import { isLutData, LUT_SIZE } from './types';

export const MAX_CUBE_BYTES = 12 * 1024 * 1024;
const clamp = (n: number) => Math.max(0, Math.min(1, n));
/** Red varies fastest, then green, then blue, as specified by the Cube format. */
export function encodeLut(sample: (r: number, g: number, b: number) => readonly number[]): string {
  const bytes = new Uint8Array(LUT_SIZE ** 3 * 6);
  const view = new DataView(bytes.buffer);
  let offset = 0;
  for (let b = 0; b < LUT_SIZE; b++) for (let g = 0; g < LUT_SIZE; g++) for (let r = 0; r < LUT_SIZE; r++) {
    for (const value of sample(r / 16, g / 16, b / 16)) {
      view.setUint16(offset, Math.round(clamp(value) * 65535), true);
      offset += 2;
    }
  }
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
}
export function decodeLut(data: string): Float32Array {
  if (!isLutData(data)) throw new Error('invalid_lut');
  const bytes = Uint8Array.from(atob(data), c => c.charCodeAt(0));
  const view = new DataView(bytes.buffer);
  const result = new Float32Array(LUT_SIZE ** 3 * 3);
  for (let i = 0; i < result.length; i++) result[i] = view.getUint16(i * 2, true) / 65535;
  return result;
}
/** Import is bounded and normalized to SDR RGB16 for a sub-48KB atomic save. */
export function importCubeLut(content: string): string {
  if (content.length > MAX_CUBE_BYTES) throw new Error('lut_too_large');
  let size = 0;
  let min = [0, 0, 0];
  let max = [1, 1, 1];
  const rows: number[] = [];
  const seen = new Set<string>();
  for (const raw of content.replace(/^\uFEFF/, '').split(/\r?\n/)) {
    const line = raw.replace(/#.*$/, '').trim();
    if (!line) continue;
    if (/^TITLE\s+".*"$/.test(line)) continue;
    const parts = line.split(/\s+/);
    const keyword = parts[0];
    if (['LUT_3D_SIZE', 'DOMAIN_MIN', 'DOMAIN_MAX'].includes(keyword)) {
      if (seen.has(keyword)) throw new Error('invalid_lut');
      seen.add(keyword);
      const values = parts.slice(1).map(Number);
      if (keyword === 'LUT_3D_SIZE') {
        size = values[0];
        if (values.length !== 1 || !Number.isInteger(size) || size < 2 || size > 65) throw new Error('invalid_lut');
      } else {
        if (values.length !== 3 || values.some(v => !Number.isFinite(v))) throw new Error('invalid_lut');
        if (keyword === 'DOMAIN_MIN') min = values;
        else max = values;
      }
      continue;
    }
    const values = parts.map(Number);
    if (values.length !== 3 || values.some(v => !Number.isFinite(v)) || rows.length >= 65 ** 3 * 3) throw new Error('invalid_lut');
    rows.push(...values);
  }
  if (!size || rows.length !== size ** 3 * 3 || max.some((v, i) => v <= min[i])) throw new Error('invalid_lut');
  return encodeLut((r, g, b) => {
    const coordinates = [r, g, b].map((v, i) => clamp((v - min[i]) / (max[i] - min[i])) * (size - 1));
    const lower = coordinates.map(Math.floor);
    const fraction = coordinates.map((v, i) => v - lower[i]);
    const result = [0, 0, 0];
    for (let z = 0; z < 2; z++) for (let y = 0; y < 2; y++) for (let x = 0; x < 2; x++) {
      const index = (Math.min(size - 1, lower[0] + x) + size * Math.min(size - 1, lower[1] + y) + size ** 2 * Math.min(size - 1, lower[2] + z)) * 3;
      const weight = (x ? fraction[0] : 1 - fraction[0]) * (y ? fraction[1] : 1 - fraction[1]) * (z ? fraction[2] : 1 - fraction[2]);
      for (let channel = 0; channel < 3; channel++) result[channel] += rows[index + channel] * weight;
    }
    return result;
  });
}
const cubeCache = new Map<string, string>();
export function lutCubeContent(data: string, strength: number): string {
  const key = `${strength}:${data}`;
  const cached = cubeCache.get(key);
  if (cached) return cached;
  const values = decodeLut(data);
  const rows = [`LUT_3D_SIZE ${LUT_SIZE}`];
  for (let i = 0; i < LUT_SIZE ** 3; i++) {
    const original = [i % 17 / 16, Math.floor(i / 17) % 17 / 16, Math.floor(i / 289) / 16];
    rows.push(original.map((v, channel) => (v + (values[i * 3 + channel] - v) * strength).toFixed(6)).join(' '));
  }
  const content = rows.join('\n');
  cubeCache.set(key, content);
  if (cubeCache.size > 8) cubeCache.delete(cubeCache.keys().next().value!);
  return content;
}
